import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { getApiUrl } from "@utils/baseUrl";
import Select, { components as RSComponents } from "react-select";
import { Tooltip } from "react-tooltip5";
import { captureException } from "@sentry/react";
import commaFormat from "@/utils/commaFormat";
import type { EnrichmentInputValues } from "../utils/types";

const contactDropdownUrl = getApiUrl("CompanyContactDetails/contactApolloDropdown");
type Opt = { label: string; value: string };

type Props = {
  show: boolean;
  onHide: () => void;
  onSubmit: (v: EnrichmentInputValues) => void;
  hasPricing: boolean;
  hasContact: boolean;
  hasCompanyInfo?: boolean;
  hasSanctions?: boolean;
  hasTraderManufacturer?: boolean;
  searchKey?: string;
  remainingCredits?: number | null;
};

const isHsCode = (s?: string) => !!s && /^\d{6}$/.test(s.trim());

const CONTACT_TYPE_OPTS: Opt[] = [
  { label: "Email", value: "email" },
  { label: "Phone", value: "phone" },
  { label: "Email & Phone", value: "both" },
];

const selectStyles = {
  control: (b: object, s: { isFocused: boolean }) => ({
    ...b,
    minHeight: 36,
    height: 36,
    borderRadius: "8px",
    fontSize: "14px",
    fontFamily: "Inter, sans-serif",
    border: s.isFocused ? "1px solid #7143f1" : "1px solid #eaecf0",
    boxShadow: s.isFocused ? "0 0 0 3px rgba(113,67,241,0.08)" : "none",
    background: "#fff",
  }),
  valueContainer: (b: object) => ({ ...b, padding: "0 12px" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (b: object) => ({ ...b, padding: "0 8px", color: "#667085" }),
  placeholder: (b: object) => ({ ...b, color: "#667085", fontSize: "14px", fontFamily: "Inter, sans-serif" }),
  singleValue: (b: object) => ({ ...b, color: "#101828", fontSize: "14px", fontFamily: "Inter, sans-serif" }),
  menu: (b: object) => ({
    ...b,
    borderRadius: "8px",
    border: "1px solid #eaecf0",
    boxShadow: "0 8px 24px rgba(16,24,40,0.1)",
    fontSize: "14px",
    fontFamily: "Inter, sans-serif",
  }),
  menuList: (b: object) => ({ ...b, maxHeight: 200 }),
  option: (b: object, { isSelected }: { isFocused: boolean; isSelected: boolean }) => ({
    ...b,
    backgroundColor: isSelected ? "rgba(111, 8, 241, 0.1)" : "transparent",
    color: "#1a1722",
    cursor: "pointer",
    fontFamily: "Inter, sans-serif",
    "&:hover": { backgroundColor: "rgba(111, 8, 241, 0.1)" },
  }),
};

const multiSelectStyles = {
  ...selectStyles,
  control: (b: object, s: { isFocused: boolean }) => ({
    ...selectStyles.control(b, s),
    height: "auto",
    minHeight: 36,
  }),
};

const errorMultiSelectStyles = {
  ...multiSelectStyles,
  control: (b: object, s: { isFocused: boolean }) => ({
    ...multiSelectStyles.control(b, s),
    border: s.isFocused ? "1px solid #7143f1" : "1px solid #f87171",
    boxShadow: s.isFocused ? "0 0 0 3px rgba(113,67,241,0.08)" : "0 0 0 3px rgba(248,113,113,0.15)",
  }),
};

const MultiValueLimit = ({ index, getValue, ...props }: any) => {
  const values = (getValue as () => Opt[])();
  const total = values.length;
  if (index > 1) {
    if (index !== 2) return null;
    const hiddenHtml = values
      .slice(2)
      .map((v: Opt) => `<span>${v.label}</span>`)
      .join("");
    return (
      <div className="tw-text-xs tw-font-medium tw-text-gray-600 tw-px-1.5 tw-bg-gray-100 tw-rounded tw-self-center tw-leading-5 tw-shrink-0 tw-cursor-default" data-tooltip-id="enrichment-overflow-tooltip" data-tooltip-html={`<div style="display:flex;flex-direction:column;gap:4px">${hiddenHtml}</div>`}>
        +{total - 2}
      </div>
    );
  }
  return <RSComponents.MultiValue {...props} index={index} getValue={getValue} />;
};

const multiComponents = { MultiValue: MultiValueLimit };

const inputCls = "tw-h-8 tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-px-3 tw-text-sm tw-text-gray-900 tw-outline-none tw-transition-all placeholder:tw-text-gray-500 focus:tw-border-primary-purple-500 focus:tw-shadow-[0_0_0_3px_rgba(113,67,241,0.08)]";

const stepBtnCls = "tw-w-11 tw-h-8 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-text-gray-900 tw-cursor-pointer tw-p-0 tw-transition-colors hover:tw-border-primary-purple-500 hover:tw-bg-primary-purple-25 hover:tw-text-primary-purple-600 disabled:tw-opacity-[0.35] disabled:tw-cursor-not-allowed";

const badgeFreeCls = "tw-text-sm tw-font-medium tw-rounded-[10px] tw-px-2.5 tw-leading-5 tw-shrink-0 tw-bg-[#d7ffe4] tw-border tw-border-[#a6ffc4] tw-text-[#05af3e]";

const badgeCreditsCls = "tw-text-xs tw-font-medium tw-rounded-[10px] tw-px-2.5 tw-leading-none tw-py-[3px] tw-shrink-0 tw-bg-[#ffe9d7] tw-border tw-border-[#ffc4a6] tw-text-[#af5505]";

const cardCls = "tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-3";
const cardTitleCls = "tw-text-sm tw-font-medium tw-text-gray-900 tw-leading-5";
const cardSubCls = "tw-text-xs tw-text-gray-900 tw-leading-4 tw-mt-0.5 tw-mb-3 tw-m-0";
const labelCls = "tw-text-sm tw-text-black tw-mb-1";
const fieldCls = "tw-flex tw-flex-col tw-flex-1 tw-min-w-0";

const EnrichmentInputsModal: React.FC<Props> = ({ show, onHide, onSubmit, hasPricing, hasContact, hasCompanyInfo = false, hasSanctions = false, hasTraderManufacturer = false, searchKey, remainingCredits }) => {
  const [hsCode, setHsCode] = useState("");
  const [deptList, setDeptList] = useState<Opt[]>([]);
  const [seniorityList, setSeniorityList] = useState<Opt[]>([]);
  const [locationList, setLocationList] = useState<Opt[]>([]);
  const [departments, setDepartments] = useState<Opt[]>([]);
  const [seniority, setSeniority] = useState<Opt[]>([]);
  const [location, setLocation] = useState<Opt[]>([]);
  const [jobTitle, setJobTitle] = useState("");
  const [contactCount, setContactCount] = useState(1);
  const [contactType, setContactType] = useState<Opt | null>(CONTACT_TYPE_OPTS[0]);
  const [pricingSelected, setPricingSelected] = useState(true);
  const [contactSelected, setContactSelected] = useState(true);
  const [companyInfoSelected, setCompanyInfoSelected] = useState(true);
  const [sanctionsSelected, setSanctionsSelected] = useState(true);
  const [traderManufacturerSelected, setTraderManufacturerSelected] = useState(true);
  const [errors, setErrors] = useState({ hsCode: false, contactFields: false });
  const hsCodeRef = useRef<HTMLInputElement>(null);
  const contactFieldsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasContact || !show) return;
    axios
      .get(contactDropdownUrl)
      .then(({ data: { message } }) => {
        setDeptList(message.department);
        setSeniorityList(message.seniority);
        setLocationList(message.location.map((x: string) => ({ label: x, value: x })));
      })
      .catch((error) => {
        captureException(error);
        toast.error("Failed to load contact options");
      });
  }, [hasContact, show]);

  useEffect(() => {
    if (show) {
      setPricingSelected(true);
      setContactSelected(true);
      setCompanyInfoSelected(true);
      setSanctionsSelected(true);
      setTraderManufacturerSelected(true);
      setErrors({ hsCode: false, contactFields: false });
      if (isHsCode(searchKey)) setHsCode(searchKey!.trim());
      return;
    }
    setHsCode("");
    setDepartments([]);
    setSeniority([]);
    setLocation([]);
    setJobTitle("");
    setContactCount(1);
    setContactType(CONTACT_TYPE_OPTS[0]);
    setErrors({ hsCode: false, contactFields: false });
  }, [show, searchKey]);

  const incCount = useCallback(() => setContactCount((n) => Math.min(5, n + 1)), []);
  const decCount = useCallback(() => setContactCount((n) => Math.max(1, n - 1)), []);

  const noneSelected = (hasPricing ? !pricingSelected : true) && (hasContact ? !contactSelected : true) && (hasCompanyInfo ? !companyInfoSelected : true) && (hasSanctions ? !sanctionsSelected : true) && (hasTraderManufacturer ? !traderManufacturerSelected : true);

  const handleSubmit = useCallback(() => {
    if (noneSelected) {
      toast.error("Please select at least one enrichment type.");
      return;
    }
    if (hasContact && contactSelected && !jobTitle && !departments.length && !seniority.length && !location.length) {
      toast.error("Input is required at least for one of the highlighted field.");
      setErrors((e) => ({ ...e, contactFields: true }));
      contactFieldsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    // HS Code is optional only when the list was created with a product search key.
    const hsCodes = hsCode
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);
    const trimmedSearchKey = (searchKey || "").trim();
    const productKeyword = trimmedSearchKey && !isHsCode(trimmedSearchKey) ? trimmedSearchKey : "";
    if (hasPricing && pricingSelected) {
      if (hsCodes.length && !hsCodes.every((c) => /^\d{6}$/.test(c))) {
        toast.error("Please enter valid 6-digit HS codes separated by commas.");
        setErrors((e) => ({ ...e, hsCode: true }));
        hsCodeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      if (hsCodes.length > 5) {
        toast.error("You can enter a maximum of 5 HS codes.");
        setErrors((e) => ({ ...e, hsCode: true }));
        hsCodeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      if (!hsCodes.length && !productKeyword) {
        toast.error("Please enter at least one 6-digit HS code.");
        setErrors((e) => ({ ...e, hsCode: true }));
        hsCodeRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
    }

    onSubmit({
      ...(hasPricing && pricingSelected ? { hsCode: hsCodes.join(","), ...(productKeyword ? { productKeyword } : {}) } : {}),
      ...(hasContact && contactSelected
        ? {
            contactParams: {
              job_title: jobTitle,
              department: departments.map((d) => d.value),
              seniority: seniority.map((s) => s.value),
              location: location.map((l) => l.value),
              requested_contacts: contactCount,
              contact_type: contactType?.value || "email",
            },
          }
        : {}),
      ...((hasCompanyInfo && companyInfoSelected) || (hasContact && contactSelected) ? { companyInfo: true } : {}),
      ...(hasSanctions && sanctionsSelected ? { sanctions: true } : {}),
      ...(hasTraderManufacturer && traderManufacturerSelected ? { traderManufacturer: true } : {}),
    });
  }, [noneSelected, hasPricing, pricingSelected, hasContact, contactSelected, hasCompanyInfo, companyInfoSelected, hasSanctions, sanctionsSelected, hasTraderManufacturer, traderManufacturerSelected, hsCode, searchKey, jobTitle, departments, seniority, location, contactCount, contactType, onSubmit]);

  return (
    <Modal show={show} onHide={onHide} centered animation={false} dialogClassName="tw-max-w-[772px] tw-w-full" contentClassName="!tw-rounded-xl !tw-border !tw-border-gray-200 !tw-shadow-[0_20px_60px_-12px_rgba(16,24,40,0.16),0_8px_24px_-8px_rgba(16,24,40,0.08)] tw-overflow-hidden tw-font-inter">
      {/* Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-4 tw-bg-white tw-border-b tw-border-gray-200">
        <span className="tw-text-base tw-font-semibold tw-text-gray-900 tw-leading-6">{hasPricing || hasContact ? "Input required for enrichment" : "Confirm enrichment"}</span>
        <span className="tw-flex tw-items-center tw-gap-6">
          {remainingCredits !== null && <span className="tw-text-sm tw-font-medium tw-rounded tw-px-2.5 tw-py-0.5 tw-leading-5 tw-shrink-0 tw-bg-gray-100 tw-border tw-border-gray-200 tw-text-gray-600">Remaining value add credits: {commaFormat(remainingCredits)}</span>}
          <button className="tw-w-5 tw-h-5 tw-border-0 tw-bg-transparent tw-text-gray-900 tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-p-0 hover:tw-opacity-50 tw-transition-opacity" onClick={onHide} aria-label="Close" type="button">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M5 5l10 10M15 5L5 15" stroke="#101828" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </span>
      </div>

      {/* Body */}
      <div className="tw-flex tw-flex-col tw-gap-4 tw-p-3 tw-bg-gray-50 tw-max-h-[70vh] tw-overflow-y-auto">
        {/* Contact details */}
        {hasContact && (
          <div className={`${cardCls} ${!contactSelected ? "tw-opacity-50" : ""}`}>
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-0.5">
              <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                <input type="checkbox" checked={contactSelected} onChange={(e) => setContactSelected(e.target.checked)} className="tw-w-4 tw-h-4 tw-accent-primary-purple-600 tw-cursor-pointer after:tw-hidden" />
                <span className={cardTitleCls}>Contact details</span>
              </label>
              <span className={badgeCreditsCls}>email: 1 and phone: 5 value add credits per company</span>
            </div>
            <div className={!contactSelected ? "tw-pointer-events-none tw-select-none" : ""}>
              <p className={cardSubCls + " tw-mb-3"}>Requires Job title, department, country</p>
              <div ref={contactFieldsRef} className="tw-flex tw-flex-col tw-gap-3">
                {/* Row 1: stepper + job title */}
                <div className="tw-flex tw-gap-4">
                  <div className={fieldCls}>
                    <label className={labelCls}>Number of contacts per company *</label>
                    <div className="tw-flex tw-items-center tw-gap-3 tw-h-8">
                      <button className={stepBtnCls} onClick={decCount} disabled={contactCount <= 1} type="button">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </button>
                      <div className="tw-flex-1 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-text-center tw-text-sm tw-text-gray-900 tw-outline-none tw-transition-colors focus:tw-border-primary-purple-500">{contactCount}</div>
                      <button className={stepBtnCls} onClick={incCount} disabled={contactCount >= 5} type="button">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className={fieldCls}>
                    <label className={labelCls}>Job title</label>
                    <input
                      className={`${inputCls} ${errors.contactFields ? "!tw-border-red-400 !tw-shadow-[0_0_0_3px_rgba(248,113,113,0.15)]" : ""}`}
                      type="text"
                      placeholder="eg. Director of procurement"
                      value={jobTitle}
                      onChange={(e) => {
                        setJobTitle(e.target.value);
                        setErrors((p) => ({ ...p, contactFields: false }));
                      }}
                    />
                  </div>
                </div>

                <div className="tw-flex tw-gap-4">
                  <div className={fieldCls}>
                    <label className={labelCls}>Select country</label>
                    <Select
                      isMulti
                      hideSelectedOptions={false}
                      options={locationList}
                      styles={errors.contactFields ? errorMultiSelectStyles : multiSelectStyles}
                      components={multiComponents}
                      value={location}
                      onChange={(v: readonly Opt[] | null) => {
                        setLocation(v ? [...v] : []);
                        setErrors((p) => ({ ...p, contactFields: false }));
                      }}
                      placeholder="Select"
                    />
                  </div>
                  <div className={fieldCls}>
                    <label className={labelCls}>Select department</label>
                    <Select
                      isMulti
                      hideSelectedOptions={false}
                      options={deptList}
                      styles={errors.contactFields ? errorMultiSelectStyles : multiSelectStyles}
                      components={multiComponents}
                      value={departments}
                      onChange={(v: readonly Opt[] | null) => {
                        setDepartments(v ? [...v] : []);
                        setErrors((p) => ({ ...p, contactFields: false }));
                      }}
                      placeholder="Select"
                    />
                  </div>
                </div>
                <div className="tw-flex tw-gap-4">
                  <div className={fieldCls}>
                    <label className={labelCls}>Select management level</label>
                    <Select
                      isMulti
                      hideSelectedOptions={false}
                      options={seniorityList}
                      styles={errors.contactFields ? errorMultiSelectStyles : multiSelectStyles}
                      components={multiComponents}
                      value={seniority}
                      onChange={(v: readonly Opt[] | null) => {
                        setSeniority(v ? [...v] : []);
                        setErrors((p) => ({ ...p, contactFields: false }));
                      }}
                      placeholder="Select"
                    />
                  </div>
                  <div className={fieldCls}>
                    <label className={labelCls}>Contact type (email, phone)</label>
                    <Select options={CONTACT_TYPE_OPTS} styles={selectStyles} value={contactType} onChange={(v) => setContactType(v || null)} placeholder="Select" />
                  </div>
                </div>
                <div className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-m-0 tw-italic">* Please fill at least one of these fields: title, country, department, or management level</div>
              </div>
            </div>

            <div className="tw-mt-6">
              <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-0.5">
                <span className={cardTitleCls}>Company information</span>
                <span className={badgeCreditsCls}>1 value add credit per company</span>
              </div>
              <p className={cardSubCls}>Competitors, Industry, Description, Domain, Revenue Range, Revenue (USD), Employee Count</p>
            </div>
          </div>
        )}

        {/* Company information */}
        {hasCompanyInfo &&
          (() => {
            const forcedByContact = hasContact && contactSelected;
            const effectiveChecked = forcedByContact || companyInfoSelected;
            return (
              <div className={`${cardCls} ${!effectiveChecked ? "tw-opacity-50" : ""} ${forcedByContact ? "tw-opacity-60" : ""}`}>
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-0.5">
                  <label className={`tw-flex tw-items-center tw-gap-2 ${forcedByContact ? "tw-cursor-not-allowed" : "tw-cursor-pointer"}`}>
                    <input type="checkbox" checked={effectiveChecked} disabled={forcedByContact} onChange={(e) => setCompanyInfoSelected(e.target.checked)} className="tw-w-4 tw-h-4 tw-accent-primary-purple-600 tw-cursor-pointer after:tw-hidden disabled:tw-cursor-not-allowed" />
                    <span className={cardTitleCls}>Company information</span>
                  </label>
                  <span className={badgeCreditsCls}>1 value add credit per company</span>
                </div>
                <p className={cardSubCls}>Competitors, Industry, Description, Domain, Revenue Range, Revenue (USD), Employee Count</p>
                {forcedByContact && <p className="tw-text-xs tw-text-gray-500 tw-mt-1 tw-m-0">Included with contact details</p>}
              </div>
            );
          })()}
        {/* Sanctions screening */}
        {hasSanctions && (
          <div className={`${cardCls} ${!sanctionsSelected ? "tw-opacity-50" : ""}`}>
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-0.5">
              <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                <input type="checkbox" checked={sanctionsSelected} onChange={(e) => setSanctionsSelected(e.target.checked)} className="tw-w-4 tw-h-4 tw-accent-primary-purple-600 tw-cursor-pointer after:tw-hidden" />
                <span className={cardTitleCls}>Sanctions screening</span>
              </label>
              <span className={badgeCreditsCls}>2 value add credits per company</span>
            </div>
            <p className={cardSubCls}>Match score, Sanctioning Program, Sanctioning Country, Citation</p>
          </div>
        )}
        {/* Trader / Manufacturer */}
        {hasTraderManufacturer && (
          <div className={`${cardCls} ${!traderManufacturerSelected ? "tw-opacity-50" : ""}`}>
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-0.5">
              <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                <input type="checkbox" checked={traderManufacturerSelected} onChange={(e) => setTraderManufacturerSelected(e.target.checked)} className="tw-w-4 tw-h-4 tw-accent-primary-purple-600 tw-cursor-pointer after:tw-hidden" />
                <span className={cardTitleCls}>Trader / Manufacturer</span>
              </label>
              <span className={badgeFreeCls}>free</span>
            </div>
            <p className={cardSubCls}>Classifies each company as a trader or a manufacturer</p>
          </div>
        )}
        {/* Per unit pricing */}
        {hasPricing && (
          <div className={`${cardCls} ${!pricingSelected ? "tw-opacity-50" : ""}`}>
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-0.5">
              <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                <input type="checkbox" checked={pricingSelected} onChange={(e) => setPricingSelected(e.target.checked)} className="tw-w-4 tw-h-4 tw-accent-primary-purple-600 tw-cursor-pointer after:tw-hidden" />
                <span className={cardTitleCls}>Per unit pricing</span>
              </label>
              <span className={badgeFreeCls}>free</span>
            </div>
            <div className={!pricingSelected ? "tw-pointer-events-none tw-select-none" : ""}>
              {searchKey && !isHsCode(searchKey) && (
                <>
                  <p className="tw-text-sm tw-text-gray-900 tw-m-0">
                    Get per unit pricing for <span className="tw-capitalize tw-font-semibold">{searchKey}</span>
                  </p>
                  <div className="tw-flex tw-items-center tw-gap-3 tw-my-1.5 tw-text-xs tw-text-gray-400">
                    <span className="tw-h-px tw-flex-1 tw-bg-gray-200" />
                    or
                    <span className="tw-h-px tw-flex-1 tw-bg-gray-200" />
                  </div>
                </>
              )}
              <div className="tw-flex tw-flex-col tw-gap-1">
                <label className={labelCls}>Provide 6 digit HS Code for your product (upto 5 HS Codes)</label>
                <input
                  ref={hsCodeRef}
                  className={`${inputCls} ${errors.hsCode ? "!tw-border-red-400 !tw-shadow-[0_0_0_3px_rgba(248,113,113,0.15)]" : ""}`}
                  type="text"
                  placeholder="e.g. 091010, 100630"
                  value={hsCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\d,]/g, "");
                    setHsCode(val);
                    setErrors((p) => ({ ...p, hsCode: false }));
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="tw-flex tw-justify-end tw-px-4 tw-py-3 tw-bg-white tw-border-t tw-border-gray-200">
        <button className="tw-h-9 tw-min-w-[74px] tw-rounded-lg tw-border-0 tw-bg-primary-purple-600 tw-px-4 tw-text-sm tw-font-medium tw-text-white tw-cursor-pointer tw-transition-all hover:tw-bg-primary-purple-800 hover:tw-shadow-[0_4px_12px_rgba(90,28,226,0.3)] active:tw-bg-[#3d0fb3] disabled:tw-opacity-50 disabled:tw-cursor-not-allowed" onClick={handleSubmit} type="button" disabled={noneSelected}>
          Enrich
        </button>
      </div>
      <Tooltip id="enrichment-overflow-tooltip" className="tw-z-50 !tw-opacity-100 !tw-text-xs" />
    </Modal>
  );
};

export default EnrichmentInputsModal;
